import { de } from '../i18n/de';
import { useUiStore } from '../app/uiStore';
import { ActivateButton } from './ActivateButton';

/**
 * `123` / `ABC` — the one control that swaps the letter board for the digit layer (`GEO-19`, `GEO-21`).
 *
 * ── Why it is here and not on the keyboard ──
 *
 * ADR-036 decided a `123` key on the letter board. Every row of `de-CH` sums to exactly eleven and the two
 * ten-column boards spend all thirty of their cells on twenty-nine letters and a space, so there is no cell to
 * take — not an expensive one, none at all, which is C79. ADR-039 moved the switch here instead, and the
 * dividend is that `LayoutKey` stays plain data: no action variant, no intent dispatch, no scripting language
 * in the layout JSON.
 *
 * ── Why it is in the band's frame and not in a tenant ──
 *
 * It replaced the `Leichter` pill, which lived inside `RepairRow` — and `RepairRow` is a *tenant*, so it is
 * handed to the candidates the moment anything is in the composer (`GEO-09`). A switch there would be gone
 * while typing, which is when a number is wanted, and the way back to the letters would be gone after typing a
 * digit. **Being unable to reach `ABC` is being unable to spell.** So `PredictionRow` owns the row and this
 * sits beside whatever tenant is showing.
 *
 * The label says what the board will be after the tap rather than what it is now, which is the same convention
 * the `Sätze`/`Sprechen` navigation button uses. `aria-pressed` carries the state for anything reading the
 * page, and the border style carries it visually, because state may never be a colour alone (`GEO-15`).
 */
export function LayerSwitch(): React.JSX.Element {
  const keyboardLayer = useUiStore((s) => s.keyboardLayer);
  const toggleKeyboardLayer = useUiStore((s) => s.toggleKeyboardLayer);
  const onDigits = keyboardLayer === 'digits';

  return (
    <ActivateButton
      id="action:keyboardLayer"
      onActivate={toggleKeyboardLayer}
      pressed={onDigits}
      // "123" read aloud is "einhundertdreiundzwanzig", which says nothing about what the button does.
      label={onDigits ? de.layerToLettersName : de.layerToDigitsName}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        fontSize: 'var(--font-body)',
        fontWeight: 700,
        background: 'var(--key-bg)',
        color: 'var(--key-fg)',
        borderWidth: 3,
        borderColor: 'var(--key-border)',
        // The one pill in the app, inherited from the toggle whose slot this is. Shape alone says this is not
        // a thing that speaks — worth more than any label to someone who is tired, and it works under any
        // colour vision (§5.2).
        borderRadius: 999,
        // State in the border as well as in aria, never in colour alone (§5.2, GEO-15).
        borderStyle: onDigits ? 'double' : 'solid',
      }}
    >
      {onDigits ? de.layerToLetters : de.layerToDigits}
    </ActivateButton>
  );
}
